import { useTranslation } from 'react-i18next'
import {
  ColumnPreviewEntry,
  ColumnsValidation,
  ColumnsValue,
  IGNORE,
  NORMALIZED_COLUMNS,
} from 'src/services/statements/columns'

interface ColumnsStepProps {
  columns: ColumnPreviewEntry[]
  value: ColumnsValue
  onChange: (rawHeader: string, choice: string) => void
  validation: ColumnsValidation
  isReadOnly?: boolean
}

/**
 * The Columns wizard step: each raw header alongside the normalized column it
 * was mapped to and a sample value, with an editable select per header so the
 * user can confirm or correct the AI-applied map.
 */
export const ColumnsStep = ({
  columns,
  value,
  onChange,
  validation,
  isReadOnly = false,
}: ColumnsStepProps) => {
  const { t } = useTranslation()

  return (
    <div data-testid="columns-step" className="space-y-4">
      <p className="text-sm text-text-secondary">{t('statementReview.columnsStepHint')}</p>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-surface-hover text-left text-text-secondary">
              <th className="px-3 py-2 font-medium">{t('statementReview.columnsHeader')}</th>
              <th className="px-3 py-2 font-medium">{t('statementReview.columnsSample')}</th>
              <th className="px-3 py-2 font-medium">{t('statementReview.columnsMappedTo')}</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((entry) => (
              <tr
                key={entry.rawHeader}
                className="border-b border-border last:border-b-0"
                data-testid={`column-row-${entry.rawHeader}`}
              >
                <td className="px-3 py-2 font-medium text-text-primary">{entry.rawHeader}</td>
                <td className="px-3 py-2 text-text-tertiary">{entry.sample || '—'}</td>
                <td className="px-3 py-2">
                  <select
                    className="w-full rounded-md border border-border-strong bg-surface p-1.5 text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
                    value={value[entry.rawHeader] ?? IGNORE}
                    disabled={isReadOnly}
                    onChange={(e) => onChange(entry.rawHeader, e.target.value)}
                    data-testid={`column-select-${entry.rawHeader}`}
                  >
                    {NORMALIZED_COLUMNS.map((col) => (
                      <option key={col} value={col}>
                        {t(`statementReview.columnName_${col}`)}
                      </option>
                    ))}
                    <option value={IGNORE}>{t('statementReview.columnIgnore')}</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!validation.valid && (
        <p className="text-sm text-error" data-testid="columns-validation-error">
          {validation.missing.length > 0
            ? t('statementReview.columnsMissingRequired', {
                columns: validation.missing
                  .map((c) => t(`statementReview.columnName_${c}`))
                  .join(', '),
              })
            : t('statementReview.columnsDuplicate', {
                columns: validation.duplicates
                  .map((c) => t(`statementReview.columnName_${c}`))
                  .join(', '),
              })}
        </p>
      )}
    </div>
  )
}
